import { useState } from "react"
import { Modal, Input, List, Avatar, Tag, Spin, Empty, Typography, Space, Alert } from "antd"
import { UserOutlined, SearchOutlined, EyeOutlined } from "@ant-design/icons"
import useFetchUsersForOversight from "../../../hooks/useFetchUsersForOversight"
import useViewModeStore from "../../../store/viewMode/useViewModeStore"
import { useAuthContext } from "../../../hooks/useAuthContext"

const { Text } = Typography

const ROLE_META = {
  admin: { label: "Admin", color: "red" },
  semiAdmin: { label: "Semi Admin", color: "volcano" },
  designer: { label: "Designer", color: "purple" },
  paperManager: { label: "Paper Manager", color: "blue" },
  accountant: { label: "Accountant", color: "green" },
  warehouse: { label: "Warehouse", color: "cyan" },
  winder: { label: "Winder", color: "gold" },
  arsomaUser: { label: "Arsoma", color: "magenta" },
  vivaUser: { label: "Viva", color: "lime" },
  tapeUser: { label: "Tape", color: "orange" },
  cutter: { label: "Cutter", color: "geekblue" },
  devSpecialist: { label: "Dev Specialist", color: "processing" },
  sales: { label: "Sales", color: "success" },
  courier: { label: "Courier", color: "default" },
}

export default function UserSwitcherModal({ open, onClose }) {
  const [search, setSearch] = useState("")
  const { users, loading, error } = useFetchUsersForOversight()
  const { enterViewMode } = useViewModeStore()
  const { user: realUser } = useAuthContext()

  // your Firestore field is `name`, not `displayName`
  const displayName = (u) => u.name || u.email || "—"

  const filtered = users.filter((u) => {
    if (u.uid === realUser?.uid) return false
    const term = search.toLowerCase()
    if (!term) return true
    return displayName(u).toLowerCase().includes(term) || u.email?.toLowerCase().includes(term)
  })

  const handleSelect = (target) => {
    enterViewMode({
      uid: target.uid,
      displayName: displayName(target), // ← uses name field
      email: target.email,
      photoURL: target.photoURL,
      roles: target.roles || {},
      firestoreData: target,
    })
    setSearch("")
    onClose()
  }

  const activeRoles = (u) =>
    Object.entries(u.roles || {})
      .filter(([, v]) => v)
      .map(([k]) => k)

  return (
    <Modal
      title={
        <Space>
          <EyeOutlined />
          View as User
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={580}>
      {error && (
        <Alert
          type="error"
          message={`Failed to load users: ${error}`}
          style={{ marginBottom: 12 }}
        />
      )}

      <Input
        placeholder="Search by name or email…"
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 12 }}
      />

      <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
        {loading ? "Loading…" : `${filtered.length} user${filtered.length !== 1 ? "s" : ""} found`}
      </Text>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : filtered.length === 0 ? (
        <Empty description="No users found" />
      ) : (
        <List
          dataSource={filtered}
          style={{ maxHeight: 480, overflowY: "auto" }}
          renderItem={(u) => (
            <List.Item
              key={u.uid}
              onClick={() => handleSelect(u)}
              style={{ cursor: "pointer", borderRadius: 6, padding: "8px 12px" }}
              actions={[<EyeOutlined key="eye" style={{ color: "#1890ff" }} />]}>
              <List.Item.Meta
                avatar={
                  <Avatar
                    src={u.photoURL}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: "#1890ff" }}
                  />
                }
                title={<Text strong>{displayName(u)}</Text>}
                description={
                  <>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {u.email}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      {activeRoles(u).map((role) => (
                        <Tag
                          key={role}
                          color={ROLE_META[role]?.color}
                          style={{ fontSize: 10, padding: "0 4px", marginBottom: 2 }}>
                          {ROLE_META[role]?.label || role}
                        </Tag>
                      ))}
                      {activeRoles(u).length === 0 && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          No roles assigned yet
                        </Text>
                      )}
                    </div>
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  )
}
